import React, { useState } from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import appConfig from 'config/app.config';
import {getScenarioUrls} from 'helpers/admin-helper';
import {errorUiTexts, adminUiTexts} from 'data/ui-texts';
import {scenarioIds, scenariosData} from 'data/scenarios-data';
import {discussionQuestions} from 'data/discussion-data';
import {customAreas} from 'data/custom-areas/custom-areas';
import Button from 'components/ui/button/button';
import './popup-edit-school.scss';

const PopupEditSchool = ({school, schools, editSchool, closePopup}) => {
	/* Input: name, email, password */
	const [name, setName] = useState(school.name ? school.name : '');
	const [email, setEmail] = useState(school.email ? school.email : '');
	const [password, setPassword] = useState(school.password ? school.password : '');

	/* Selected scenario ids and their qr urls */
	const [selectedScenarioIds, setSelectedScenarioIds] = useState(school.scenarioIds ? school.scenarioIds : []);
	const [scenarioQrUrls, setScenarioQrUrls] = useState(getScenarioUrls(school));

	/* Background image */
	const [backgroundImage, setBackgroundImage] = useState(school.backgroundImage ? school.backgroundImage : null);
	const [backgroundImageFile, setBackgroundImageFile] = useState(null);

	/* Custom area */
	const [customAreaId, setCustomAreaId] = useState(school.customAreaId ? school.customAreaId : null);

	/* Custom questions */
	let customQuestions = JSON.parse(JSON.stringify(discussionQuestions));
	if (school.questions) {
		customQuestions = school.questions;
	}
	const [questions, setQuestions] = useState(customQuestions);

	/* Facilitator survey link */
	const [facilitatorSurveyLink, setFacilitatorSurveyLink] = 
		useState(school.facilitatorSurveyLink ? school.facilitatorSurveyLink : null);

	/* Link to short games */
	const [showLinkToShortHTX, setShowLinkToShortHTX] = 
		useState(school.showLinkToShortHTX ? school.showLinkToShortHTX : false);
	const [showLinkToShortHHX, setShowLinkToShortHHX] = 
		useState(school.showLinkToShortHHX ? school.showLinkToShortHHX : false);

	/* Loading status */
	const [isLoading, setIsLoading] = useState(false);

	/* Error message */
	const [errorMessage, setErrorMessage] = useState(null);




	/**
	 * Handles form input
	 * @param {object} event
	 */
	const handleInput = (event) => {
		setErrorMessage(null);
		if (event.target.name === 'name') {
			setName(event.target.value);
		} else if (event.target.name === 'email') {
			setEmail(event.target.value);
		} else if (event.target.name === 'password') {
			setPassword(event.target.value);
		} else if (event.target.name.includes('question')) {
			const questionIdArr = event.target.name.split('-');
			if (questionIdArr.length === 2) {
				const questionIndex = parseInt(questionIdArr[1]);
				if (questions.length > questionIndex) {
					const newQuestions = JSON.parse(JSON.stringify(questions));
					newQuestions[questionIndex].text = event.target.value;
					setQuestions(newQuestions);
				}
			}
		} else if (event.target.name === 'facilitatorSurveyLink') {
			setFacilitatorSurveyLink(event.target.value);
		} else if (event.target.name === 'customArea') {
			setCustomAreaId(event.target.value);
		}
	};


	/**
	 * Handles image file upload
	 * @param {object} event
	 */
	const handleImageUpload = (event) => {
		setErrorMessage(null);
		const file = event.target.files[0];
		if (file) {
			// Check file size (1MB = 1048576 bytes)
			if (file.size > 1048576) {
				setErrorMessage(errorUiTexts.imageTooLarge);
				event.target.value = null;
				return;
			}
			// Check file type
			if (!file.type.startsWith('image/')) {
				setErrorMessage('Filen skal være et billede.');
				event.target.value = null;
				return;
			}
			setBackgroundImageFile(file);
		}
	};

	/**
	 * Toggle if a question is unlimited
	 * @param {number} questionIndex 
	 */
	const toggleQuestionLimit = (questionIndex) => {
		if (questions.length > questionIndex) {
			const newQuestions = JSON.parse(JSON.stringify(questions));
			newQuestions[questionIndex].isUnlimited = (newQuestions[questionIndex].isUnlimited === true ? false : true);
			setQuestions(newQuestions);
		}
	};

	/** Add questions */
	const addQuestion = () => {
		let newQuestions = JSON.parse(JSON.stringify(questions));
		let newId = newQuestions.length + 1;
		let newIdIsValid = false;
		while (!newIdIsValid) {
			// eslint-disable-next-line no-loop-func
			if (newQuestions.some((q) => {return q.id === newId;})) {
				newId += 1;
			} else {
				newIdIsValid = true;
			}
		}
		newQuestions.push({id: newId, text: ''});
		setQuestions(newQuestions);
		
	};
	/**
	 * Delete questions
	 * @param {number} questionIndex 
	 */
	const deleteQuestion = (questionIndex) => {
		let newQuestions = JSON.parse(JSON.stringify(questions));
		if (newQuestions.length > questionIndex) {
			newQuestions.splice(questionIndex, 1);
			setQuestions(newQuestions);
		}
	};

	/**
	 * Reset questions
	 */
	const resetQuestions = () => {
		setQuestions(JSON.parse(JSON.stringify(discussionQuestions)));
	};


	/**
	 * Toggle scenario id
	 * @param {string} scenarioId 
	 */
	const toggleScenarioId = (scenarioId) => {
		let newSelectedScenarioIds = JSON.parse(JSON.stringify(selectedScenarioIds));
		const scenarioIndex = newSelectedScenarioIds.indexOf(scenarioId);
		if (scenarioIndex >= 0) {
			newSelectedScenarioIds.splice(scenarioIndex, 1);
		} else {
			newSelectedScenarioIds.push(scenarioId);
		}
		setSelectedScenarioIds(newSelectedScenarioIds);
	};

	/**
	 * Custom scenario qr urls
	 * @param {string} scenarioId 
	 * @param {object} e 
	 */
	const updateScenarioQrUrl = (scenarioId, e) => {
		const newScenarioQrUrls = JSON.parse(JSON.stringify(scenarioQrUrls));
		const scenarioIndex = newScenarioQrUrls.findIndex((s) => {
			return s.scenarioId === scenarioId;
		});
		if (scenarioIndex >= 0) {
			newScenarioQrUrls[scenarioIndex].qrUrl = e.target.value;
			setScenarioQrUrls(newScenarioQrUrls);
		}

	};

	/**
	 * Create school
	 */
	const handleEditSchool = () => {
		if (isLoading) return;

		setIsLoading(true);
		setErrorMessage(null);

		let newErrMsg = null;

		/* Check required input */
		if (!name || !email || !password) {
			newErrMsg = errorUiTexts.emptyFields;
		/* Check school name */
		} else if (schools && schools.length > 0) {
			if (schools.some((s) => {
				return (
					s.name.trim().toLowerCase() === name.trim().toLowerCase() &&
					s.id !== school.id
				);
			})) {
				newErrMsg = errorUiTexts.schoolNameExists;
			} else {
				/* Check scenario urls */
				selectedScenarioIds.forEach((scenarioId) => {
					const scenarioQrUrl = scenarioQrUrls.find((s) => {return s.scenarioId === scenarioId;});
					if (scenarioQrUrl) {
						if (!scenarioQrUrl.qrUrl || scenarioQrUrl.qrUrl.length === 0) {
							newErrMsg = errorUiTexts.noQRUrl;
						}
					}
				});
			}
		}

		if (newErrMsg) {
			setErrorMessage(newErrMsg);
			setIsLoading(false);
		} else {
			const validQuestions = questions.filter((q) => {return q.text && q.text.length > 0;});



			// Upload image to Firebase Storage if present
			const processImageAndSubmit = () => {
				if (backgroundImageFile) {
					const storageRef = firebase.storage().ref();
					// Create unique filename with timestamp to prevent overwrites
					const timestamp = Date.now();
					const fileExtension = backgroundImageFile.name.split('.').pop();
					const uniqueFileName = `${school.id}_${timestamp}.${fileExtension}`;
					const imageRef = storageRef.child(appConfig.backgroundImagesFolder + '/' + uniqueFileName);
					const contentType = backgroundImageFile.type;

					imageRef.put(backgroundImageFile, {contentType}).then((snapshot) => {
						// Get the download URL after successful upload
						return snapshot.ref.getDownloadURL();
					}).then((downloadURL) => {
						// Submit with the download URL
						submitEditSchool(downloadURL);
					}).catch((error) => {
						console.error('Upload error:', error);
						setErrorMessage('Fejl ved upload af billede.');
						setIsLoading(false);
					});
				} else {
					// No new file, keep existing background image
					submitEditSchool(backgroundImage);
				}
			};

			const submitEditSchool = (backgroundImage) => {
				editSchool(
					school.id,
					name,
					email,
					password,
					selectedScenarioIds,
					scenarioQrUrls,
					validQuestions,
					facilitatorSurveyLink,
					showLinkToShortHTX,
					showLinkToShortHHX,
					customAreaId ? customAreaId : null,
					(backgroundImage ? backgroundImage : null)
				).then((response) => {
					if (response.status === 'success') {
						closePopup();
					} else {
						setIsLoading(false);
						console.error(response);
					}
				});
			};

			processImageAndSubmit();
		}
	};

	return (
		<div className='PopupEditSchool' onClick={() => {closePopup();}}>
			<div className='PopupEditSchool-window' onClick={(e) => {e.stopPropagation();}}>
				<div className='PopupEditSchool-title'>
					<span>{adminUiTexts.createSchoolPopup.title2}</span>
				</div>
				<form className='PopupEditSchool-form' onSubmit={handleEditSchool}>
					<div className="PopupEditSchool-columns">
						<div className="PopupEditSchool-column left">
							<div className={'PopupEditSchool-input'}>
								<div className='PopupEditSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.schoolName}</span>
								</div>
								<input
									name='name'
									type='text'
									placeholder={adminUiTexts.createSchoolPopup.school}
									autoComplete='section-inputAnswer name'
									value={name ? name : ''}
									onChange={(event)=>{handleInput(event);}}
								/>
							</div>
							<div className={'PopupEditSchool-input'}>
								<div className='PopupEditSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.email}</span>
								</div>
								<input
									name='email'
									type='text'
									placeholder={adminUiTexts.createSchoolPopup.email}
									autoComplete='section-inputAnswer email'
									value={email ? email : ''}
									onChange={(event)=>{handleInput(event);}}
								/>
							</div>
							<div className={'PopupEditSchool-input'}>
								<div className='PopupEditSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.password}</span>
								</div>
								<input
									name='password'
									type='text'
									placeholder={adminUiTexts.createSchoolPopup.password}
									autoComplete='section-inputAnswer password'
									value={password ? password : ''}
									onChange={(event)=>{handleInput(event);}}
								/>
							</div>
							<div className="PopupEditSchool-input scenarios">
								<div className='PopupEditSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.games}</span>
								</div>
								<div className="PopupEditSchool-scenarios">
									{scenarioIds.map((scenarioId) => {
										const scenarioData = scenariosData[scenarioId];
										if (!scenarioData) return null;

										const scenarioQrUrl = scenarioQrUrls.find((s) => {
											return s.scenarioId === scenarioId;
										});
										return (
											<div 
												key={scenarioId} 
												className={'PopupEditSchool-scenario' + 
													(selectedScenarioIds.includes(scenarioId) ? ' selected' : '')}
											>
												<div 
													className="PopupEditSchool-scenarioName"
													onClick={() => {toggleScenarioId(scenarioId);}}
												>
													<span>{scenarioData.title}</span>
												</div>
												<div className="PopupEditSchool-scenarioQrUrl">
													<input
														disabled={!selectedScenarioIds.includes(scenarioId)}
														name={'qrUrl-' + scenarioId}
														type='text'
														placeholder={adminUiTexts.createSchoolPopup.qrUrl}
														autoComplete='section-inputAnswer qrUrl'
														value={scenarioQrUrl && scenarioQrUrl.qrUrl 
															? scenarioQrUrl.qrUrl : ''}
														onChange={(event)=>{updateScenarioQrUrl(scenarioId, event);}}
													/>
												</div>
											</div>
										);
									})}
								</div>
							</div>
							<div className="PopupEditSchool-facilitatorSurveyLink">
								<div className={'PopupEditSchool-input facilitatorSurveyLink'}>
									<div className='PopupEditSchool-inputTitle'>
										<span>{adminUiTexts.createSchoolPopup.facilitatorSurvey}</span>
									</div>
									<input
										name='facilitatorSurveyLink'
										type='text'
										autoComplete='section-inputAnswer facilitatorSurvey'
										value={facilitatorSurveyLink ? facilitatorSurveyLink : ''}
										onChange={(event)=>{handleInput(event);}}
									/>
								</div>
							</div>
							<div className="PopupEditSchool-linkToShortGames">
								<div 
									className={'PopupEditSchool-linkToShortGame htx' 
										+ (showLinkToShortHTX ? ' selected' : '')}
									onClick={() => {setShowLinkToShortHTX(!showLinkToShortHTX);}}
								>
									<span>{adminUiTexts.createSchoolPopup.linkToShortHTX}</span>
								</div>
								<div 
									className={'PopupEditSchool-linkToShortGame htx' 
										+ (showLinkToShortHHX ? ' selected' : '')}
									onClick={() => {setShowLinkToShortHHX(!showLinkToShortHHX);}}
								>
									<span>{adminUiTexts.createSchoolPopup.linkToShortHHX}</span>
								</div>
							</div>
							<div className="PopupEditSchool-customArea">
								<div className={'PopupEditSchool-input customArea'}>
									<div className='PopupEditSchool-inputTitle'>
										<span>{adminUiTexts.createSchoolPopup.customArea}</span>
									</div>
									<select
										name='customArea'
										value={customAreaId ? customAreaId : ''}
										onChange={(event)=>{handleInput(event);}}
									>
										<option value=''>Ingen</option>
										{customAreas.map((area) => {
											return (
												<option key={area.id} value={area.id}>
													{area.name}
												</option>
											);
										})}
									</select>
								</div>
							</div>
						</div>
						<div className="PopupEditSchool-column right">
							<div className="PopupEditSchool-questionsTitle">
								<span>{adminUiTexts.createSchoolPopup.questions} ({questions.length})</span>
							</div>
							<div className="PopupEditSchool-addQuestionBtn" onClick={addQuestion} />
							<div className="PopupEditSchool-resetQuestionsBtn" onClick={resetQuestions} />
							<div className="PopupEditSchool-questions">
								{questions.map((q, index) => {
									return (
										<div key={index} className="PopupEditSchool-question">
											<div className="PopupEditSchool-toggleWrap">
												<div 
													className={'PopupEditSchool-questionUnlimitedToggle' 
														+ (q.isUnlimited ? ' checked' : '')} 
													onClick={() => {toggleQuestionLimit(index);}}
												/>
											</div>
											<input 
												name={'question-' + index}
												value={q.text ? q.text : ''}
												onChange={(event) => {handleInput(event);}}
											/>
											<div 
												className="PopupEditSchool-deleteQuestionBtn" 
												onClick={() => {deleteQuestion(index);}}
											/>
										</div>
									);
								})}
							</div>							
						</div>
					</div>

					<div className="PopupEditSchool-urlAndBgImage">
						<div className="PopupEditSchool-bgImage">
							<div className={'PopupEditSchool-input'}>
								<div className='PopupEditSchool-inputTitle'>
									<span>{adminUiTexts.createSchoolPopup.schoolBackgroundImage}</span>
									<span className="PopupEditSchool-fileSizeLimit">
										({adminUiTexts.createSchoolPopup.max1Mb})
									</span>
								</div>
								<input
									name='backgroundImage'
									type='file'
									accept='image/*'
									onChange={(event)=>{handleImageUpload(event);}}
								/>
								
							</div>
							{backgroundImage &&
								<div className="PopupEditSchool-currentBgImage">
									<span>
										{adminUiTexts.createSchoolPopup.currentImage}
									</span>
									<img
										src={backgroundImage}
										alt="Current background"
										className="PopupEditSchool-thumbnail"
									/>
									<div 
										className="PopupEditSchool-removeCurrentImage" 
										onClick={() => {setBackgroundImage(null);}} 
									><span>X Fjern</span></div>
								</div>
							}
						</div>
					</div>
						

					<div className="PopupEditSchool-submitButton">
						<Button
							isDisabled={isLoading}
							text={adminUiTexts.createSchoolPopup.save}
							classes={['login']}
							onClick={() => {handleEditSchool();}}
						/>
					</div>
				</form>

				{errorMessage &&
					<div className='PopupEditSchool-errorMessage'>
						<span>{errorMessage}</span>
					</div>
				}
			</div>
		</div>
	);
};

PopupEditSchool.propTypes = {
	school: PropTypes.object.isRequired,
	schools: PropTypes.array.isRequired,
	editSchool: PropTypes.func.isRequired,
	closePopup: PropTypes.func.isRequired,
};

export default PopupEditSchool;